import type { HanziText } from "@/data/model";
import {
  createCollection,
  eq,
  localOnlyCollectionOptions,
  useLiveQuery,
} from "@tanstack/react-db";
import { View } from "@/client/ui/View";
import { CompactWordRows } from "./CompactWordRows";
import { useDb } from "./hooks/useDb";
import { WikiTitledBox } from "./WikiTitledBox";
import { arrayFilterUnique } from "@pinyinly/lib/collections";
import { useId, useMemo } from "react";
import { getCharacterItemsForWord } from "./WikiHanziWordCharacters.utils";

export function WikiHanziWordCharacters({ hanzi }: { hanzi: HanziText }) {
  const db = useDb();
  const collectionId = useId();

  const charactersCollection = useMemo(
    () =>
      createCollection(
        localOnlyCollectionOptions({
          id: `wikiHanziWordCharacters:${collectionId}`,
          initialData: getCharacterItemsForWord(hanzi),
          getKey: (item) => item.hanzi,
        }),
      ),
    [collectionId, hanzi],
  );

  const { data: entriesWithDupes } = useLiveQuery({
    query: (q) =>
      q
        .from({ character: charactersCollection })
        .join({ entry: db.dictionaryCollection }, ({ character, entry }) =>
          eq(character.hanzi, entry.hanzi),
        )
        .orderBy(({ character }) => character.position, `asc`)
        .orderBy(({ entry }) => entry.hanziWord, `asc`)
        .select(({ entry, character }) => ({
          hanzi: character.hanzi,
          hanziWord: entry.hanziWord,
          hsk: entry.hsk,
          hskFirstAppearance: entry.hskFirstAppearance,
          gloss: entry.gloss,
          pinyin: entry.pinyin,
        })),
  });

  const entries = entriesWithDupes.filter(arrayFilterUnique((x) => x.hanzi));

  if (entries.length < 2) {
    return null;
  }

  return (
    <WikiTitledBox title="Characters">
      <View className="p-3">
        <CompactWordRows dictionaryEntries={entries} />
      </View>
    </WikiTitledBox>
  );
}
